import { z } from 'zod';
import { ApiError, createApiClient } from './api';
import { SessionView } from './sessionView';

export { BusinessUnit, SessionView } from './sessionView';

/**
 * The Nest account API (apps/account/bff, the session authority at /account/api): sign-in, the session ("who am I"),
 * business-unit scope, sign-out and password recovery. Schemas mirror @infinix/server auth/routes.ts.
 */

const Message = z.object({ message: z.string() });
export const TenantPage = z.object({
  tenants: z.array(z.object({ id: z.string(), name: z.string(), status: z.string().nullable() })),
  page: z.number().int(),
  totalPages: z.number().int(),
  total: z.number().int(),
});
export type TenantPage = z.infer<typeof TenantPage>;
const name = z.string().nullable();
export const Profile = z.object({ email: z.string(), firstName: name, lastName: name, phone: name });
export type Profile = z.infer<typeof Profile>;
const client = createApiClient('/account/api');

export const accountApi = {
  /** The current session, or null when signed out. */
  async session(): Promise<SessionView | null> {
    try {
      return await client.request(SessionView, 'GET', '/session', undefined, { sessionCall: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'AUTH_REQUIRED') return null;
      throw err;
    }
  },
  signIn(email: string, password: string): Promise<SessionView> {
    return client.request(SessionView, 'POST', '/auth/login', { email, password }, { sessionCall: true });
  },
  /** Ends the session at the backend; rejects when it did not, so the person is never told they are signed out. */
  signOut(): Promise<void> {
    return client.send('POST', '/auth/logout', undefined, { sessionCall: true });
  },
  forgotPassword(email: string): Promise<{ message: string }> {
    return client.request(Message, 'POST', '/auth/forgot-password', { email }, { sessionCall: true });
  },
  /** The signed-in person's own name and phone, from Core. */
  profile(): Promise<Profile> {
    return client.request(Profile, 'GET', '/profile');
  },
  /** The signed-in person changes their own password (current + new). */
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return client.send('POST', '/auth/change-password', { currentPassword, newPassword });
  },
  resetPassword(code: string, newPassword: string): Promise<void> {
    return client.send('POST', '/auth/reset-password', { code, newPassword }, { sessionCall: true });
  },
  selectBusinessUnit(businessUnitId: string | null): Promise<SessionView> {
    return client.request(SessionView, 'PUT', '/session/bu', { businessUnitId });
  },
  /** Staff tenant picker (Core's staff route set, N-7). */
  tenants(q: string, page = 1): Promise<TenantPage> {
    const qs = new URLSearchParams({ ...(q ? { q } : {}), page: String(page) }).toString();
    return client.request(TenantPage, 'GET', `/session/tenants?${qs}`);
  },
  selectTenant(organizationId: string | null): Promise<SessionView> {
    return client.request(SessionView, 'PUT', '/session/tenant', { organizationId });
  },
};
